import { Box, Grid, GridItem, Text } from '@chakra-ui/react';
import { ReactNode } from 'react';
import {
  BranchIdentifier,
  getBranchColor,
} from '@texas/utils/helpers/texas/branchHelpers';

interface TeamMatrixBranch {
  id: number;
  identifier: string;
  name: string;
}

export function TeamMatrixGrid({
  branchCount,
  children,
}: {
  branchCount: number;
  children: ReactNode;
}) {
  return (
    <Grid
      columnGap={1}
      overflow="auto"
      rowGap={2}
      templateColumns={`112px repeat(${branchCount}, 220px)`}
      sx={{
        '.sticky-team-matrix-item': {
          bg: 'hsl(from var(--chakra-colors-texas-bg-dark) h s l / 0.9)',
          left: 0,
          position: 'sticky',
          zIndex: 1,
          _light: {
            bg: 'hsl(from var(--chakra-colors-white) h s l / 0.9)',
          },
        },
      }}
    >
      {children}
    </Grid>
  );
}

export function TeamMatrixCorner() {
  return <GridItem className="sticky-team-matrix-item" />;
}

export function TeamMatrixBranchHeader({
  branch,
}: {
  branch: TeamMatrixBranch;
}) {
  return (
    <GridItem px={3} py={2}>
      <Box display="flex" alignItems="baseline" gap={2}>
        <Text variant="header-medium" fontSize="md">
          {branch.identifier}
        </Text>
        <Box
          bg={getBranchColor(branch.identifier as BranchIdentifier)}
          borderRadius="full"
          boxSize={3}
        />
      </Box>
      <Text fontSize="sm" opacity={0.7} variant="header">
        {branch.name}
      </Text>
    </GridItem>
  );
}

export function TeamMatrixRoleHeader({
  description,
  rowIndex,
  title,
}: {
  description?: string;
  rowIndex: number;
  title: string;
}) {
  return (
    <GridItem
      alignItems="center"
      className="sticky-team-matrix-item"
      display="flex"
      gridColumn={1}
      gridRow={rowIndex}
      px={3}
      py={2}
    >
      <Box>
        <Text fontFamily="barlow" fontSize="sm" fontWeight="semibold">
          {title}
        </Text>
        {description && (
          <Text fontSize="2xs" opacity={0.7}>
            {description}
          </Text>
        )}
      </Box>
    </GridItem>
  );
}

export function TeamMatrixCell({
  children,
  columnIndex,
  rowIndex,
}: {
  children: ReactNode;
  columnIndex: number;
  rowIndex: number;
}) {
  return (
    <GridItem
      bg="whiteAlpha.100"
      gridColumn={columnIndex}
      gridRow={rowIndex}
      minH="76px"
      p={3}
      role="group"
      _light={{ bg: 'gray.50' }}
    >
      {children}
    </GridItem>
  );
}
